import { useEffect, useRef, useState } from "react";
import { Mic, Send, Square, Bell, BellOff, Loader2, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { fileUrl, formatApiError, uploadImage } from "@/lib/api";

function Bubble({ m, mine, onImgLoad, onDelete }) {
  return (
    <div className={`group flex items-center gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
      {onDelete && mine && (
        <button onClick={() => onDelete(m)} aria-label="Elimina messaggio" data-testid={`chat-delete-${m.id}`}
          className="h-8 w-8 shrink-0 grid place-items-center rounded-full text-[var(--muted)] hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      )}
      <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[var(--ink)] text-white rounded-br-md" : "bg-white border border-[var(--border)] rounded-bl-md"}`}>
        {!mine && <p className="text-xs font-semibold text-[var(--primary-hover)] mb-0.5">{m.sender_name}</p>}
        {m.image_path && (
          <a href={fileUrl(m.image_path)} target="_blank" rel="noreferrer" className="block mt-1" data-testid={`chat-image-${m.id}`}>
            <img src={fileUrl(m.image_path)} alt="Foto" onLoad={onImgLoad} className="rounded-xl max-h-72 w-auto max-w-full object-cover" />
          </a>
        )}
        {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
        {m.audio_path && <audio controls preload="none" src={fileUrl(m.audio_path)} className="mt-1 max-w-full h-10" data-testid={`chat-audio-${m.id}`} />}
        <p className={`text-[10px] mt-1 text-right ${mine ? "text-zinc-400" : "text-[var(--muted)]"}`}>
          {new Date(m.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
      {onDelete && !mine && (
        <button onClick={() => onDelete(m)} aria-label="Elimina messaggio" data-testid={`chat-delete-${m.id}`}
          className="h-8 w-8 shrink-0 grid place-items-center rounded-full text-[var(--muted)] hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      )}
    </div>
  );
}

function useRecorder(onDone) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4", "audio/ogg"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = (r.mimeType || mime || "audio/webm").split(";")[0];
        onDone(new Blob(chunks, { type }), type);
      };
      r.start();
      rec.current = r;
      setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setRecording(true);
    } catch {
      toast.error("Microfono non disponibile: consenti l'accesso al microfono");
    }
  };
  const stop = () => {
    clearInterval(timer.current);
    rec.current?.stop();
    setRecording(false);
  };
  return { recording, seconds, start, stop };
}

export default function Chat() {
  const { user } = useAuth();
  const { messages, send, remove, setActive, soundOn, setSoundOn } = useChat();
  const onDelete = user.role === "admin" ? async (m) => {
    if (!window.confirm("Eliminare questo messaggio per tutti?")) return;
    try {
      await remove(m.id);
      toast.success("Messaggio eliminato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  } : null;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const photoRef = useRef(null);

  const sendPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      try {
        await send({ image_path: await uploadImage(f) });
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    setBusy(false);
  };

  useEffect(() => {
    setActive(true);
    return () => setActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [messages.length]);

  const sendAudio = async (blob, type) => {
    setBusy(true);
    try {
      const ext = type.includes("mp4") ? "m4a" : type.split("/")[1];
      const path = await uploadImage(blob, `vocale.${ext}`);
      await send({ audio_path: path });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const { recording, seconds, start, stop } = useRecorder(sendAudio);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await send({ text });
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-12rem)] lg:h-[calc(100dvh-6rem)]">
      <div className="flex items-end justify-between gap-4 mb-4 fade-up">
        <div>
          <p className="eyebrow">Team</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Chat</h1>
        </div>
        <button onClick={() => setSoundOn(!soundOn)} className="btn-secondary h-10 px-4" data-testid="chat-sound-toggle">
          {soundOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />} {soundOn ? "Suono attivo" : "Suono spento"}
        </button>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto rounded-2xl bg-[var(--surface-2)] p-3 sm:p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && <p className="text-center text-sm text-[var(--muted)] py-10" data-testid="chat-empty">Nessun messaggio. Scrivi il primo al team.</p>}
        {messages.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === user.id} onDelete={onDelete} onImgLoad={() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight })} />)}
      </div>
      <form onSubmit={submit} className="mt-3 flex items-end gap-2">
        {recording ? (
          <div className="flex-1 h-12 rounded-full bg-red-50 border border-red-200 text-red-700 flex items-center gap-2 px-4 text-sm font-semibold" data-testid="chat-recording-indicator">
            <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse" /> Registrazione... {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
          </div>
        ) : (
          <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Messaggio..."
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
            className="flex-1 min-h-12 max-h-32 resize-none rounded-3xl border border-[var(--border)] bg-white px-4 py-3 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
            data-testid="chat-text-input" />
        )}
        {!recording && (
          <>
            <button type="button" onClick={() => photoRef.current.click()} disabled={busy} aria-label="Invia foto"
              className="h-12 w-12 shrink-0 rounded-full grid place-items-center bg-white border border-[var(--border)] text-[var(--ink)]" data-testid="chat-photo-button">
              <Camera className="h-5 w-5" />
            </button>
            <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={sendPhoto} data-testid="chat-photo-input" />
          </>
        )}
        <button type="button" onClick={recording ? stop : start} disabled={busy} aria-label={recording ? "Ferma e invia" : "Registra audio"}
          className={`h-12 w-12 shrink-0 rounded-full grid place-items-center transition-colors ${recording ? "bg-red-600 text-white" : "bg-white border border-[var(--border)] text-[var(--ink)]"}`} data-testid="chat-record-button">
          {recording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        {!recording && (
          <button type="submit" disabled={busy || !text.trim()} className="btn-primary h-12 w-12 shrink-0 p-0" aria-label="Invia" data-testid="chat-send-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        )}
      </form>
    </div>
  );
}
